// Media files. A MediaItem's bytes live outside the tree (db.ts), under a key made from a
// hash of the contents, so the same file added twice (or to two trees) is stored once.

import type { MediaKind } from './types';

/** A content key for a file: the first 128 bits of its SHA-256, in hex. */
export async function fileKey(bytes: Uint8Array): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
    return 'f' + [...digest.subarray(0, 16)].map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  // No Web Crypto (an insecure origin): a random key still works, just without de-duplication.
  return 'f' + Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
}

export function mediaKindOf(type: string, name = ''): MediaKind {
  if (type.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif|tiff?|bmp)$/i.test(name)) return 'image';
  if (type === 'application/pdf' || /\.pdf$/i.test(name)) return 'pdf';
  if (type.startsWith('audio/') || /\.(mp3|m4a|wav|aiff?|ogg|flac)$/i.test(name)) return 'audio';
  if (type.startsWith('video/') || /\.(mp4|mov|m4v|webm|avi)$/i.test(name)) return 'video';
  return 'file';
}

/** A MIME type from a file name, for files that arrive without one (packages, zips). */
export function mimeFromName(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const known: Record<string, string> = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif',
    tif: 'image/tiff', tiff: 'image/tiff', bmp: 'image/bmp', pdf: 'application/pdf', mp3: 'audio/mpeg', m4a: 'audio/mp4', wav: 'audio/wav',
    aif: 'audio/aiff', aiff: 'audio/aiff', ogg: 'audio/ogg', mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm',
    txt: 'text/plain', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  };
  return known[ext] ?? 'application/octet-stream';
}

/** Files carried alongside a tree on import or in a backup, by key. */
export type FileBag = Map<string, Blob>;
